/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { PropertiesService } from "@services/requests";

/**
 * POSTs *file* to a presigned target, reporting progress from 0 to 1.
 *
 * Uses XMLHttpRequest rather than fetch because only XHR exposes upload
 * progress, and a video of up to 1 GB on a phone connection takes minutes:
 * without a moving bar the drawer looks frozen.
 *
 * @param target - The presigned `{ url, fields }` from the backend.
 * @param file - The video file, sent last under the `file` key as S3 requires.
 * @param onProgress - Receives the uploaded fraction.
 * @returns Resolves once storage accepted the object.
 */
function postWithProgress(
  target: { url: string; fields: Record<string, string> },
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  const formData = new FormData();
  Object.entries(target.fields).forEach(([key, value]) => formData.append(key, value));
  formData.append("file", file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", target.url);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error(`Video upload failed with status ${xhr.status}`)));
    xhr.onerror = () => reject(new Error("Video upload failed (network)"));
    xhr.send(formData);
  });
}

/**
 * Uploads one video file to a property.
 *
 * The backend refuses the link (400 type/size, 409 limit) before anything
 * is sent, so a rejected file fails fast instead of after a long upload.
 *
 * @returns The stored video id.
 */
export async function uploadPropertyVideo(
  agencyId: string,
  propertyId: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<string> {
  const link = await PropertiesService.getPropertyVideoUploadLink(agencyId, propertyId, file.type, file.size);
  await postWithProgress(link.upload, file, onProgress);
  return link.video_id;
}
